/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================
*/

import { useState, useEffect, useMemo, useRef } from "react";

// react-router components
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";

// Material Dashboard 3 PRO React examples
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 3 PRO React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 3 PRO React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 3 PRO React routes
import routes, {
  signInCoverRoute,
  detailAsetRoute,
  userOverviewRoute,
  detailKinerjaRoute,
  perorangDetailRoute,
  perorangDetailApelRoute,
  detailKinerjaDinasRoute,
  kinerjaPimpinanPDDetailRoute,
  perdinasDetailLaporanRoute,
  apelDinasSuperAdminRoute,
  kinerjaDisposisiDetailRoute,
  kinerjaTugasDetailRoute,
  instruksiDetailRoute,
  disposisiDetailRoute,
  eventSayaDetailRoute,
  laporanEventDetailRoute,
} from "routes";

// Public Survey Form (No Authentication Required)
import FormSurvey from "layouts/pages/survey-kepuasan/form-survey";
import FormSurveyPublik from "layouts/pages/survey-kepuasan/form-survey-publik";
import FormBukuTamuPublik from "layouts/pages/buku-tamu/form-buku-tamu-publik";
import DaftarHadirEventPublik from "layouts/pages/events/daftar-hadir-publik";
import DownloadRSS from "layouts/pages/download-rss";

// Kinerja Organisasi
import DetailKinerjaBidang from "layouts/pages/kinerja-organisasi/index";
import DetailKinerjaIndividu from "layouts/pages/kinerja-organisasi/detail-individu/index";
import ProtectedRoute from "components/ProtectedRoute";

// Material Dashboard 3 PRO React contexts
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Utils
import axios from "axios";
import { API_ESA_OFFICE } from "utils";
import { setupAxiosInterceptor } from "utils/tokenUtils";
import { useSessionTimeout } from "hooks/useSessionTimeout";
import { useStatusEOCheck } from "hooks/useStatusEOCheck";

const ESA_OFFICE_SIDENAV_LOGO = `${process.env.PUBLIC_URL || ""}/esa_office_logo_sidebar.png`;

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Check if current route is public survey route or public buku tamu route or public event route or download RSS route
  const isPublicSurveyRoute =
    pathname.startsWith("/survey/") ||
    pathname.startsWith("/publik/survey-kepuasan/") ||
    pathname.startsWith("/publik/buku-tamu/") ||
    pathname.startsWith("/publik/events/") ||
    pathname.startsWith("/download/rote-smart-service");

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Check login: redirect ke dashboard jika user sudah login dan ada di halaman sign-in
  // Exclude public routes (survey form). Cegah spam redirect dengan ref.
  const lastRedirectAt = useRef(0);
  useEffect(() => {
    if (isPublicSurveyRoute) return;

    const user = localStorage.getItem("user");
    if (!user || pathname !== "/authentication/sign-in/cover") return;

    const now = Date.now();
    if (now - lastRedirectAt.current < 2000) return; // Batasi redirect max 1x per 2 detik
    lastRedirectAt.current = now;

    navigate("/dashboards/analytics", { replace: true });
  }, [pathname, navigate, isPublicSurveyRoute]);

  // Setup global axios interceptor: tambah Authorization header + handle 401 dengan refresh token (sekali saat mount)
  useEffect(() => {
    axios.defaults.withCredentials = true;
    setupAxiosInterceptor(navigate);
  }, [navigate]);

  // Session timeout: redirect ke login setelah 30 menit tidak aktif
  useSessionTimeout(navigate);

  // Pengecekan berkala status_EO - logout otomatis jika akun dinonaktifkan
  useStatusEOCheck(navigate);

  // Ambil data user sekali di sini
  const userStorage = localStorage.getItem("user");
  const user = userStorage ? JSON.parse(userStorage) : null;
  const userStatus = user?.status;
  const userProfil = user?.profil;
  const levelJabatan = user?.level_jabatan;

  // Fungsi pengecekan route berdasarkan allowedStatuses, allowedUidRSS (OR jika keduanya ada), dan allowedLevelJabatan
  const checkRouteAvailability = (route, userStatus, levelJabatan, uidRSS) => {
    const hasStatuses =
      route.allowedStatuses && route.allowedStatuses.length > 0;
    const hasUidRSS = route.allowedUidRSS && route.allowedUidRSS.length > 0;

    if (hasStatuses && hasUidRSS) {
      const statusOk = route.allowedStatuses.includes(userStatus);
      const uidOk = route.allowedUidRSS.includes(uidRSS);
      if (!statusOk && !uidOk) {
        return false;
      }
    } else {
      if (hasStatuses && !route.allowedStatuses.includes(userStatus)) {
        return false;
      }
      if (hasUidRSS && !route.allowedUidRSS.includes(uidRSS)) {
        return false;
      }
    }

    // Cek allowedLevelJabatan jika ada
    if (route.allowedLevelJabatan && route.allowedLevelJabatan.length > 0) {
      if (!levelJabatan || !route.allowedLevelJabatan.includes(levelJabatan)) {
        return false;
      }
    }

    return route.available !== false; // default true
  };

  // Filter routes berdasarkan user status, uid_RSS, dan level_jabatan
  const filterAvailableRoutes = (routes, userStatus, levelJabatan, uidRSS) => {
    return routes.filter((route) => {
      if (!checkRouteAvailability(route, userStatus, levelJabatan, uidRSS)) {
        return false;
      }

      if (route.collapse) {
        const filteredCollapse = filterAvailableRoutes(
          route.collapse,
          userStatus,
          levelJabatan,
          uidRSS
        );
        if (filteredCollapse.length === 0) {
          return false;
        }
        route.collapse = filteredCollapse;
      }

      return true;
    });
  };

  const getRoutes = (allRoutes) => {
    const routesList = [];

    allRoutes.forEach((route) => {
      if (route.collapse) {
        // Rekursif untuk collapse items
        const nestedRoutes = getRoutes(route.collapse);
        routesList.push(...nestedRoutes);
      }

      if (route.route) {
        routesList.push(
          <Route key={route.key} path={route.route} element={route.component} />
        );
      }
    });

    return routesList;
  };

  // Get filtered routes for both Routes and Sidenav
  // Skip filtering if on public survey route
  const availableRoutes = isPublicSurveyRoute
    ? []
    : filterAvailableRoutes(routes, userStatus, levelJabatan, user?.uid_RSS);

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && !isPublicSurveyRoute && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={ESA_OFFICE_SIDENAV_LOGO}
              brandName=""
              routes={availableRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && !isPublicSurveyRoute && <Configurator />}
        <Routes>
          {/* Public Route - Form Survey (No Authentication Required) - Must be first */}
          <Route path="/survey/:id" element={<FormSurvey />} />
          <Route
            path="/publik/survey-kepuasan/:id"
            element={<FormSurveyPublik />}
          />
          <Route
            path="/publik/buku-tamu/:link"
            element={<FormBukuTamuPublik />}
          />
          {getRoutes(availableRoutes)}
          <Route
            path={signInCoverRoute.route}
            element={signInCoverRoute.component}
          />
          <Route
            path={detailAsetRoute.route}
            element={detailAsetRoute.component}
          />
          <Route
            path={userOverviewRoute.route}
            element={userOverviewRoute.component}
          />
          <Route
            path={detailKinerjaRoute.route}
            element={detailKinerjaRoute.component}
          />
          <Route
            path={perorangDetailRoute.route}
            element={perorangDetailRoute.component}
          />
          <Route
            path={perorangDetailApelRoute.route}
            element={perorangDetailApelRoute.component}
          />
          <Route
            path={detailKinerjaDinasRoute.route}
            element={detailKinerjaDinasRoute.component}
          />
          <Route
            path={perdinasDetailLaporanRoute.route}
            element={perdinasDetailLaporanRoute.component}
          />
          <Route
            path={apelDinasSuperAdminRoute.route}
            element={apelDinasSuperAdminRoute.component}
          />
          <Route
            path={kinerjaDisposisiDetailRoute.route}
            element={kinerjaDisposisiDetailRoute.component}
          />
          <Route
            path={kinerjaTugasDetailRoute.route}
            element={kinerjaTugasDetailRoute.component}
          />
          <Route
            path={instruksiDetailRoute.route}
            element={instruksiDetailRoute.component}
          />
          <Route
            path="*"
            element={
              pathname.startsWith("/survey/") ? (
                <FormSurvey />
              ) : pathname.startsWith("/publik/survey-kepuasan/") ? (
                <FormSurveyPublik />
              ) : pathname.startsWith("/publik/buku-tamu/") ? (
                <FormBukuTamuPublik />
              ) : (
                <Navigate to="/authentication/sign-in/cover" />
              )
            }
          />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && !isPublicSurveyRoute && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={ESA_OFFICE_SIDENAV_LOGO}
            brandName=""
            routes={availableRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
        </>
      )}
      {layout === "vr" && !isPublicSurveyRoute && <Configurator />}
      <Routes>
        {/* Public Route - Form Survey (No Authentication Required) - Must be first */}

        <Route
          path="/publik/survey-kepuasan/:id"
          element={<FormSurveyPublik />}
        />
        <Route
          path="/publik/buku-tamu/:link"
          element={<FormBukuTamuPublik />}
        />
        <Route
          path="/publik/events/daftar-hadir/:eventId"
          element={<DaftarHadirEventPublik />}
        />
        <Route path="/download/rote-smart-service" element={<DownloadRSS />} />
        {getRoutes(availableRoutes)}
        {/* Route untuk detail kinerja bidang */}
        <Route
          path="/kinerja-organisasi/bidang/:bidangId/detail"
          element={
            <ProtectedRoute>
              <DetailKinerjaBidang />
            </ProtectedRoute>
          }
        />
        {/* Route untuk detail kinerja individu perhari */}
        <Route
          path="/kinerja-organisasi/bidang/:bidangId/individu/:uid_RSS/detail"
          element={
            <ProtectedRoute>
              <DetailKinerjaIndividu />
            </ProtectedRoute>
          }
        />
        <Route
          path={signInCoverRoute.route}
          element={signInCoverRoute.component}
        />
        <Route
          path={detailAsetRoute.route}
          element={detailAsetRoute.component}
        />
        <Route
          path={userOverviewRoute.route}
          element={userOverviewRoute.component}
        />
        <Route
          path={detailKinerjaRoute.route}
          element={detailKinerjaRoute.component}
        />
        <Route
          path={detailKinerjaDinasRoute.route}
          element={detailKinerjaDinasRoute.component}
        />
        <Route
          path={kinerjaPimpinanPDDetailRoute.route}
          element={kinerjaPimpinanPDDetailRoute.component}
        />
        <Route
          path={perorangDetailRoute.route}
          element={perorangDetailRoute.component}
        />
        <Route
          path={perorangDetailApelRoute.route}
          element={perorangDetailApelRoute.component}
        />
        <Route
          path={perdinasDetailLaporanRoute.route}
          element={perdinasDetailLaporanRoute.component}
        />
        <Route
          path={apelDinasSuperAdminRoute.route}
          element={apelDinasSuperAdminRoute.component}
        />
        <Route
          path={kinerjaDisposisiDetailRoute.route}
          element={kinerjaDisposisiDetailRoute.component}
        />
        <Route
          path={kinerjaTugasDetailRoute.route}
          element={kinerjaTugasDetailRoute.component}
        />
        <Route
          path={instruksiDetailRoute.route}
          element={instruksiDetailRoute.component}
        />
        <Route
          path={disposisiDetailRoute.route}
          element={disposisiDetailRoute.component}
        />
        <Route
          path={eventSayaDetailRoute.route}
          element={eventSayaDetailRoute.component}
        />
        <Route
          path={laporanEventDetailRoute.route}
          element={laporanEventDetailRoute.component}
        />
        <Route
          path="*"
          element={
            isPublicSurveyRoute ? (
              <FormSurvey />
            ) : (
              <Navigate to="/authentication/sign-in/cover" />
            )
          }
        />
      </Routes>
    </ThemeProvider>
  );
}
